import React, { useEffect, useState, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FiCalendar } from "react-icons/fi";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import "../ContactRequest/ContactRequest.css";
import axios from "axios";
import DeletePopup from "../Components/DeletePopup";
import { deleteContact, getContact } from "../../Services/BackendUrl";
import { encryptedAuth } from "../../Services/EncryptData";

const ContactRequests = ({ setIsExporting }) => {
  const [searchText, setSearchText] = useState("");
  const [customers, setCustomers] = useState([]);
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState(customers);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [isMultipleDelete, setIsMultipleDelete] = useState(false);
  const [selectedLocalid, setSelectedLocalid] = useState(null);
  const [contactCustomers, setContactCustomers] = useState([]);

  const fetchallContactRequests = useCallback(async () => {
    try {
      const response = await axios.get(getContact, {
        headers: {
          "Content-Type": "application/json",
          Authorization : `Encrypted ${encryptedAuth}`
        },
      });

      setContactCustomers(response.data.contacts);
    } catch (error) {
      console.error("Error fetching enquiry user:",error);
    } finally {
      setIsExporting(false);
    }
  }, [setIsExporting]);

  useEffect(() => {
    fetchallContactRequests();
  }, [fetchallContactRequests]);

  useEffect(() => {
    if (contactCustomers.length > 0) {
      const formatted = contactCustomers.map((customer, index) => ({
        ...customer,
        fullName: `${customer.firstName || ""} ${
          customer.lastName || ""
        }`.trim(),
        localid: (index + 1).toString().padStart(2, "0"),
      }));
      setCustomers(formatted);
    }
  }, [contactCustomers]);

const handleSearch = (e) => {
  const value = e.target.value;
  setSearchText(value);
};

const filteredData =
  searchText.trim() === ""
    ? customers
    : customers.filter((customer) => {
        const search = searchText.toLowerCase();

        const nameMatch = customer.fullName?.toLowerCase().includes(search);
        const emailMatch = customer.email?.toLowerCase().includes(search);
        const contactMatch = customer.contactNumber
          ?.toLowerCase()
          .includes(search);
        const messageMatch = customer.message?.toLowerCase().includes(search);
        const createdAtMatch = customer.createdAt
          ?.toLowerCase()
          .includes(search);

        return (
          nameMatch ||
          emailMatch ||
          contactMatch ||
          messageMatch ||
          createdAtMatch
        );
      });



  const deleteMultipleContacts = () => {
    setIsMultipleDelete(true);
    setShowDeletePopup(true);
  };

  const handleDeleteSingle = (LocalId) => {
    setShowDeletePopup(true);
    setSelectedLocalid(LocalId);
    setIsMultipleDelete(false);
  };

  const confirmDelete = async () => {
    const customer = customers.find((c) => c.localid === selectedLocalid);

    if (!customer) {
      console.error("Customer not found for local ID:", selectedLocalid);
      return;
    }
    const serverId = [];
    serverId.push(customer.id);

    try {
      const response = await axios.post(
        deleteContact,
        { ids: serverId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization : `Encrypted ${encryptedAuth}`
          },
        }
      );

      if (response.data.message) {
        const remainingCustomers = customers.filter(
          (customer) => !serverId.includes(String(customer.id))
        );

        const reindexed = remainingCustomers.map((c, i) => ({
          ...c,
          localid: (i + 1).toString().padStart(2, "0"),
          checkbox: false,
        }));

        setCustomers(reindexed);
      }
    } catch (error) {
      console.error("Error deleting enquiry user:", error);
    } finally {
      setShowDeletePopup(false);
    }
  };

  const showCustomerData = (rowData) => {
    setSelectedCustomer(rowData);
  };

  const actionBodyTemplate = (rowData) => (
    <div className="flex items-center justify-center gap-3 px-3 py-3">
  <button
    className="text-lg bg-[#2D4A5E] px-3 py-2 border-none rounded-lg flex items-center justify-center"
    onClick={() => showCustomerData(rowData)}
  >
    <i className="pi pi-eye text-cyan-400 text-base"></i>
  </button>

  <button
    className="text-lg bg-[#2D4A5E] border-none px-3 py-2 rounded-lg cursor-pointer flex items-center justify-center"
    onClick={() => handleDeleteSingle(rowData.localid)}
  >
    <i className="pi pi-trash text-red-400 text-base"></i>
  </button>
</div>

  );

  const confirmMultipleDelete = async () => {
    if (showCheckboxes) {
      const selectedServerIds = customers
        .filter((c) => selectedRows.includes(c.localid))
        .map((c) => c.id);

      if (selectedServerIds.length === 0) {
        alert("No items selected for deletion.");
        return;
      }

      try {
        const response = await axios.post(
          deleteContact,
          { ids: selectedServerIds },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization : `Encrypted ${encryptedAuth}`
            },
          }
        );

        if (response.data.message) {
          const remainingCustomers = customers.filter(
            (customer) => !selectedServerIds.includes(String(customer.id))
          );

          const reindexed = remainingCustomers.map((c, i) => ({
            ...c,
            localid: (i + 1).toString().padStart(2, "0"),
            checkbox: false,
          }));

          setCustomers(reindexed);
          setSelectedRows([]);
          setShowCheckboxes(false);
        }
      } catch (error) {
        console.error("Error deleting enquiry users:", error.response?.data || error.message);
      } finally {
        setShowDeletePopup(false);
      }
    } else {
      const updated = customers.map((c) => ({ ...c, checkbox: true }));
      setCustomers(updated);
      setShowCheckboxes(true);
    }
  };

  const checkboxBodyTemplate = (rowData) => {
    if (!rowData.checkbox) return null;

    return (
      <Checkbox
        checked={selectedRows.includes(rowData.localid)}
        onChange={(e) => {
          const isChecked = e.checked;

          setSelectedRows((prevSelected) => {
            const newSelected = isChecked
              ? [...prevSelected, rowData.localid]
              : prevSelected.filter((localid) => localid !== rowData.localid);

            const updatedCustomers = customers.map((c) =>
              c.localid === rowData.localid ? { ...c, isChecked: isChecked } : c
            );
            setCustomers(updatedCustomers);

            const updatedFiltered = filteredCustomers.map((c) =>
              c.localid === rowData.localid ? { ...c, isChecked: isChecked } : c
            );
            setFilteredCustomers(updatedFiltered);

            return newSelected;
          });
        }}
      />
    );
  };

  return (
    <div className="lg:px-1">
      <div className="bg-[#7FC5FF26] rounded-full px-5 py-3 flex flex-wrap items-center justify-between gap-4 text-white mb-4">
        <div className="flex items-center justify-center gap-3">
          <div className="text-white text-[20px] font-lufga">
            <FiCalendar className="text-white text-[40px]" />
          </div>

          <p className="font-semibold text-white text-[21px] font-lufga">
            Contact Requests
          </p>
        </div>

        <div className="searchbox-container">
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              placeholder="Search here"
              value={searchText}
              onChange={handleSearch}
            />
          </IconField>
        </div>
      </div>

      <div className="rounded-xl  py-[10px]   h-[550px] contact-table-container">
        <DataTable
          value={filteredData}
          dataKey="id"
          scrollable
          style={{ width: "100%" }}
          paginator
          rows={5}
          rowsPerPageOptions={[5, 10, 25]}
          paginatorTemplate={{
            layout: "PrevPageLink PageLinks NextPageLink",

            PrevPageLink: (options) => {
              return (
                <button
                  type="button"
                  className="p-paginator-prev p-paginator-element p-link"
                  onClick={options.onClick}
                  disabled={options.disabled}
                >
                  <i
                    className="pi pi-angle-left"
                    style={{ marginRight: "5px" }}
                  ></i>
                  Back
                </button>
              );
            },
            NextPageLink: (options) => {
              return (
                <button
                  type="button"
                  className="p-paginator-next p-paginator-element p-link"
                  onClick={options.onClick}
                  disabled={options.disabled}
                >
                  Next
                  <i
                    className="pi pi-angle-right"
                    style={{ marginLeft: "5px" }}
                  ></i>
                </button>
              );
            },
          }}
          className="custom-colour text-white font-lufga"
        >
          <Column
            field="name"
            header="Name"
            style={{ width: "150px" }}
            headerStyle={{
              backgroundColor: "transparent",
              color: "#fff",
              border: "#7FC5FF26",
              borderTopLeftRadius: "25px",
              borderBottomLeftRadius: "25px",
              padding: "15px 10px 15px 10px",
              textAlign: "center",
            }}
          />
          <Column
            field="email"
            header="Email ID"
            headerStyle={{
              backgroundColor: "transparent",
              color: "#fff",
              border: "#7FC5FF26",
              padding: "15px 10px 15px 10px",
              textAlign: "center",
            }}
          />
          <Column
            field="contactNumber"
            header="Contact Number"
            headerStyle={{
              backgroundColor: "transparent",
              color: "#fff",
              border: "#7FC5FF26",
              padding: "15px 10px 15px 10px",
              textAlign: "center",
            }}
          />
          <Column
            field="message"
            header="Message"
            body={(rowData) => (
              <div className="truncate-cell">{rowData.message}</div>
            )}
            headerStyle={{
              backgroundColor: "transparent",
              color: "#fff",
              border: "#7FC5FF26",
              padding: "15px 10px 15px 10px",
              textAlign: "center",
            }}
          />

          <Column
            field="createdAt"
            header="Submitted On"
            headerStyle={{
              backgroundColor: "transparent",
              color: "#fff",
              border: "#7FC5FF26",
              padding: "15px 10px 15px 10px",
              textAlign: "center",
            }}
            body={(rowData) => {
              const date = new Date(rowData.createdAt);
              const formatted = date
                .toLocaleDateString("en-GB")
                .replace(/\//g, "-"); 
              return <span>{formatted}</span>;
            }}
          />
          <Column
            header="Actions"
            body={(rowData) => actionBodyTemplate(rowData)}
            headerStyle={{
              backgroundColor: "transparent",
              color: "#fff",
              border: "#7FC5FF26",
              padding: "15px 10px 15px 10px",
              textAlign: "center",
            }}
          />
          <Column
            header={
              <Button
                style={{
                  backgroundColor: "transparent",
                  border: "transparent",
                }}
                icon={
                  <i
                    className={`pi ${
                      showCheckboxes ? "pi-trash" : "pi-pencil"
                    }`}
                    style={{
                      color: showCheckboxes ? "#FF4649" : "inherit",
                    }}
                  />
                }
                onClick={() => {
                  if (showCheckboxes) {
                    const anyChecked = customers.some((c) => c.isChecked);

                    if (anyChecked) {
                      deleteMultipleContacts();
                    } else {
                      setShowCheckboxes(false);

                      const updated = customers.map((c) => ({
                        ...c,
                        checkbox: false,
                        isChecked: false,
                      }));

                      setCustomers(updated);
                      setSelectedRows([]);
                    }
                  } else {
                    const updated = customers.map((c) => ({
                      ...c,
                      checkbox: true,
                    }));
                    setCustomers(updated);
                    setShowCheckboxes(true);
                  }
                }}
                severity={showCheckboxes ? "danger" : "info"}
              />
            }
            body={checkboxBodyTemplate}
            headerStyle={{
              backgroundColor: "transparent",
              color: "#fff",
              border: "#7FC5FF26",
              borderTopRightRadius: "25px",
              borderBottomRightRadius: "25px",
              padding: "15px 10px 15px 10px",
              textAlign: "center",
            }}
          />
        </DataTable>
      </div>

      {selectedCustomer && (
        <div className="contact-overlay">
          <div className="contact-card">
            <div className="contact-header">
              <div className="contact-namepof">
                <h1>{selectedCustomer.name?.toUpperCase()}</h1>
                <p>{selectedCustomer.email}</p>
              </div>
              <div>
                <Button
                  icon="pi pi-times"
                  onClick={() => setSelectedCustomer(null)}
                  className="p-button-danger"
                />
              </div>
            </div>
            <div className="body-container">
              <div className="customer-submitted">
                <p className="submitted-title">
                  <strong>Submitted On</strong>
                </p>
                <p className="submitted">
                  {new Date(selectedCustomer.createdAt)
                    .toLocaleDateString("en-GB")
                    .replace(/\//g, "-")}
                </p>
              </div>
              <div className="customer-number">
                <p>
                  <strong>Contact Number</strong>
                </p>
                <div className="number">
                  <p className="contactnumber">
                    {selectedCustomer.contactNumber}
                  </p>
                </div>
              </div>
            </div>
            <div className="customer-contact">
              <p className="feedback-title">
                <strong>Message</strong>
              </p>
              <p className="contactmessage">{selectedCustomer.message}</p>
            </div>
          </div>
        </div>
      )}

      <DeletePopup
        visible={showDeletePopup}
        onHide={() => {
          setShowDeletePopup(false);
          setShowCheckboxes(false);
          const updated = customers.map((c) => ({
            ...c,
            checkbox: false,
            isChecked: false,
          }));
          setCustomers(updated);
          const updatedFiltered = filteredCustomers.map((c) => ({
            ...c,
            checkbox: false,
            isChecked: false,
          }));
          setFilteredCustomers(updatedFiltered);
          setSelectedRows([]);
        }}
        onConfirm={isMultipleDelete ? confirmMultipleDelete : confirmDelete}
      />
    </div>
  );
};

export default ContactRequests;